import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { hasFeature, FREE_TRADE_LIMIT, FREE_AI_CHAT_LIMIT } from '../utils/planFeatures';
import { getPlanFromCache, setPlanCache } from '../utils/planCache';

export const usePlan = () => {
  const [plan, setPlan] = useState(getPlanFromCache() || 'free');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPlan = async () => {
      if (!auth.currentUser) { setLoading(false); return; }

      // Show cached plan immediately — no flash
      const cached = getPlanFromCache();
      if (cached) setPlan(cached);

      try {
        // Primary source: subscriptions/{uid} (existing data)
        const subSnap = await getDoc(doc(db, 'subscriptions', auth.currentUser.uid));
        if (subSnap.exists()) {
          const data = subSnap.data();
          const freshPlan = data.planId || data.plan || 'free';
          // Validate plan is active and not expired
          const isActive = data.status === 'active' &&
            (!data.endDate || new Date(data.endDate) >= new Date());
          const resolvedPlan = isActive ? freshPlan : 'free';
          setPlan(resolvedPlan);
          setPlanCache(resolvedPlan);
        } else {
          // Fallback: users/{uid}.plan
          const userSnap = await getDoc(doc(db, 'users', auth.currentUser.uid));
          if (userSnap.exists()) {
            const freshPlan = userSnap.data().plan || 'free';
            setPlan(freshPlan);
            setPlanCache(freshPlan);
          }
        }
      } catch (e) {
        console.error('[usePlan]', e);
      } finally {
        setLoading(false);
      }
    };
    loadPlan();
  }, []);

  return {
    plan,
    loading,
    isFree:  plan === 'free',
    isPro:   plan === 'pro',
    isElite: plan === 'elite',
    can:        (feature) => hasFeature(plan, feature),
    tradeLimit: plan === 'free' ? FREE_TRADE_LIMIT : Infinity,
    aiChatLimit: plan === 'free' ? FREE_AI_CHAT_LIMIT : Infinity,
  };
};
